import { DayOfWeek } from '@fluentui/date-time-utilities';
import { getUserOptions } from 'owa-session-store';
import {
    OwaDate,
    startOfWeek,
    getDay,
    formatMonthDay,
    formatDateInterval,
    formatMonthName,
    startOfMonth,
    endOfMonth,
    isAfter,
    isBefore,
    addMonths,
    endOfWeek,
} from 'owa-datetime';
import type { DateRange } from 'owa-datetime-utils';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { getTzOrUserTimeZone, getOwaDate } from 'owa-datepicker-common';
import { observableToday } from 'owa-observable-datetime';

export function getFirstDayOfWeek() {
    return getUserOptions()?.WeekStartDay || 0;
}
export function getStartOfWeek(selectedDate: OwaDate) {
    return startOfWeek(selectedDate, getFirstDayOfWeek());
}

export function isSelectedDayLastOfWeek(selectedDate: OwaDate) {
    const firstDayOfWeek = getFirstDayOfWeek();
    const selectedDayOfWeek: DayOfWeek = Number(getDay(selectedDate));
    return firstDayOfWeek == 0
        ? selectedDayOfWeek == DayOfWeek.Saturday
        : selectedDayOfWeek == firstDayOfWeek - 1;
}

export function getWorkDays() {
    const userOptionsWorkDays = getUserOptions()?.WorkingHours?.WorkDays;
    return userOptionsWorkDays ? Number(userOptionsWorkDays) : 127; // 127 default is bit mask of 2^7
}

export function formatDateForStrip(dateRange: DateRange, dateRangeType: DateRangeType) {
    if (dateRangeType == DateRangeType.Month) {
        return formatMonthName(dateRange.start);
    }

    const sameDay = getDay(dateRange.start) == getDay(dateRange.end);

    return sameDay
        ? formatMonthDay(dateRange.start)
        : formatDateInterval(dateRange.start, dateRange.end);
}

export function getFirstWeekOfYear() {
    return getUserOptions()?.FirstWeekOfYear || 1;
}

export function getShowWeekNumbers() {
    return getUserOptions()?.ShowWeekNumbers ?? false;
}

/**
 * Returns true if newNavDate is out of the DayPicker month range, the DayPicker month range being the dates
 * from the currentNavDate's full month, the previous and next month's dates that overlap weeks with dates
 * in currentNavDate's month
 * @param currentNavDate the navigated date in the current month shown in the DayPicker
 * @param newNavDate the navigated date we are checking is included in currentNavDate's month range
 * @param isTopFocus whether the newNavDate is is in the top month picker in the calendar asset month view component
 */
export function isDateOutOfMonthDayPickerRange(
    currentNavDate: OwaDate,
    newNavDate: OwaDate,
    isTopFocus: boolean
) {
    const isFuture = isAfter(newNavDate, currentNavDate);

    let monthStart;
    let monthEnd;

    if (!isTopFocus) {
        monthStart = startOfMonth(addMonths(currentNavDate, 1));
        monthEnd = endOfMonth(addMonths(currentNavDate, 1));
    } else {
        monthStart = startOfMonth(currentNavDate);
        monthEnd = endOfMonth(currentNavDate);
    }

    const monthStartFirstDayOfWeek = getStartOfWeek(monthStart);

    if (isFuture) {
        const endMonthRange = endOfWeek(monthEnd, getFirstDayOfWeek());
        return isAfter(newNavDate, endMonthRange);
    } else {
        return isBefore(newNavDate, monthStartFirstDayOfWeek);
    }
}

export function getTodayTz() {
    const today = observableToday();
    return getTzOrUserTimeZone(today) || 'UTC';
}

export function convertToOwaDate(date: Date) {
    const tz = getTodayTz();
    return getOwaDate(tz, date);
}
